// Pure hide-override matching — mirrors functions/src/availability/hide-overrides.js
// (CommonJS there, ESM here). Same semantics: design doc §4.4.
//
// Kept in lockstep manually because both packages need the same pure logic
// and the file is small + stable. If it diverges materially, extract into
// a shared package.
//
// B-7: `isEventHidden` (boolean any-match) and `findMatchingOverrides` (array of
// every matching override doc, for the Unhide cleanup path) derive from ONE
// private predicate so they can never disagree about whether an override hides
// an event. `findMatchingOverrides` previously lived in HideEventDialog with a
// near-identical-but-not-identical copy of this logic.

// The event's instance anchor: recurring instances key off originalStartTime,
// falling back to startUtc.
function instanceAnchorOf(event) {
  return event.originalStartTime || event.startUtc || null;
}

// True iff a single override doc currently hides the given event.
function overrideMatchesEvent(ovr, event, isRecurring, anchor) {
  if (!ovr || typeof ovr !== "object") return false;
  if (ovr.calendarId !== event.calendarId) return false;
  if (!ovr.scope) return false;

  if (ovr.scope === "single") {
    if (isRecurring) {
      return !!(
        ovr.recurringEventId &&
        ovr.recurringEventId === event.recurringEventId &&
        ovr.instanceStart &&
        ovr.instanceStart === anchor
      );
    }
    return !!(ovr.eventId && ovr.eventId === event.eventId);
  }

  if (ovr.scope === "thisAndFuture") {
    return !!(
      isRecurring &&
      ovr.recurringEventId &&
      ovr.recurringEventId === event.recurringEventId &&
      ovr.instanceStart &&
      anchor &&
      anchor >= ovr.instanceStart
    );
  }

  return false;
}

export function isEventHidden(event, overrides) {
  if (!event) return false;
  if (!Array.isArray(overrides) || overrides.length === 0) return false;
  const isRecurring = !!event.recurringEventId;
  const anchor = instanceAnchorOf(event);
  return overrides.some((ovr) =>
    overrideMatchesEvent(ovr, event, isRecurring, anchor)
  );
}

/**
 * Every hide-override doc that currently matches `event` — used by the Unhide
 * path to delete all of them. Shares `overrideMatchesEvent` with isEventHidden,
 * so `findMatchingOverrides(e, o).length > 0` is always equivalent to
 * `isEventHidden(e, o)`.
 */
export function findMatchingOverrides(event, overrides) {
  if (!event) return [];
  const isRecurring = !!event.recurringEventId;
  const anchor = instanceAnchorOf(event);
  return (overrides || []).filter((ovr) =>
    overrideMatchesEvent(ovr, event, isRecurring, anchor)
  );
}
